import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Upload, Quote } from "lucide-react";

const emptyForm = { quote: "", name: "", role: "", image_url: "", active: true, order: 0 };

export const TestimonialsManager = ({ client, API }) => {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => client.get(`${API}/admin/testimonials`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ quote: t.quote, name: t.name, role: t.role || "", image_url: t.image_url || "", active: t.active, order: t.order || 0 });
    setDialogOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post(`${API}/admin/upload`, fd);
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Foto berhasil diunggah");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.quote.trim() || !form.name.trim()) {
      toast.error("Kutipan dan nama wajib diisi");
      return;
    }
    setSaving(true);
    const payload = { ...form, quote: form.quote.trim(), name: form.name.trim(), order: Number(form.order) || 0 };
    try {
      if (editing) {
        await client.put(`${API}/admin/testimonials/${editing.testimonial_id}`, payload);
        toast.success("Testimoni diperbarui");
      } else {
        await client.post(`${API}/admin/testimonials`, payload);
        toast.success("Testimoni ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan testimoni");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Hapus testimoni dari "${t.name}"?`)) return;
    try {
      await client.delete(`${API}/admin/testimonials/${t.testimonial_id}`);
      toast.success("Testimoni dihapus");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus testimoni");
    }
  };

  return (
    <div className="mt-16" data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl tracking-tight flex items-center gap-3">
          <Quote className="w-5 h-5 text-[#A67C1E]" /> Testimoni Pelanggan
        </h2>
        <Button data-testid="add-testimonial-button" onClick={openNew} className="bg-[#A67C1E] text-[#FBF7EE] hover:bg-[#211B10] font-medium">
          <Plus className="w-4 h-4 mr-2" /> Tambah Testimoni
        </Button>
      </div>

      <div className="border border-[#211B10]/10 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#211B10]/10 hover:bg-transparent">
              <TableHead className="text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Nama</TableHead>
              <TableHead className="text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Kutipan</TableHead>
              <TableHead className="text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Status</TableHead>
              <TableHead className="text-right text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.testimonial_id} data-testid={`testimonial-row-${t.testimonial_id}`} className="border-[#211B10]/10 hover:bg-[#211B10]/[0.04]">
                <TableCell>
                  <div className="flex items-center gap-3">
                    {t.image_url && <img src={t.image_url} alt={t.name} className="w-8 h-8 object-cover border border-[#211B10]/15" />}
                    <div>
                      <p className="text-sm font-medium">{t.name}</p>
                      <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#211B10]/55">{t.role}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="max-w-md">
                  <p className="text-xs text-[#211B10]/60 truncate">{t.quote}</p>
                </TableCell>
                <TableCell>
                  <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-1 border ${t.active ? "text-[#1B4332] border-[#1B4332]/30 bg-[#1B4332]/5" : "text-[#211B10]/50 border-[#211B10]/15"}`}>
                    {t.active ? "Tampil" : "Disembunyikan"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-2">
                    <Button data-testid={`edit-testimonial-${t.testimonial_id}`} variant="outline" size="sm" onClick={() => openEdit(t)} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`delete-testimonial-${t.testimonial_id}`} variant="outline" size="sm" onClick={() => handleDelete(t)} className="border-[#211B10]/15 bg-transparent hover:bg-red-100 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow className="border-[#211B10]/10 hover:bg-transparent">
                <TableCell colSpan={4} className="text-center py-10 text-[#211B10]/60 text-sm" data-testid="testimonials-table-empty">
                  Belum ada testimoni. Klik "Tambah Testimoni" untuk memulai.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="testimonial-dialog" className="bg-white border-[#211B10]/10 text-[#211B10] max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">
              {editing ? "Edit Testimoni" : "Tambah Testimoni"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label htmlFor="t-quote" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Kutipan *</Label>
              <Textarea id="t-quote" data-testid="testimonial-quote-input" value={form.quote} onChange={set("quote")} rows={4} placeholder="Apa kata pelanggan Anda..." className="bg-[#FBF7EE] border-[#211B10]/15" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="t-name" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Nama *</Label>
                <Input id="t-name" data-testid="testimonial-name-input" value={form.name} onChange={set("name")} placeholder="cth: Budi Santoso" className="bg-[#FBF7EE] border-[#211B10]/15" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="t-role" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Peran / Kota</Label>
                <Input id="t-role" data-testid="testimonial-role-input" value={form.role} onChange={set("role")} placeholder="cth: Wirausaha, Surabaya" className="bg-[#FBF7EE] border-[#211B10]/15" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-image" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Foto (opsional)</Label>
              <div className="flex gap-3">
                <Input id="t-image" data-testid="testimonial-image-input" value={form.image_url} onChange={set("image_url")} placeholder="https://... atau unggah file" className="bg-[#FBF7EE] border-[#211B10]/15 flex-1" />
                <input type="file" id="t-image-file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" />
                <Button type="button" data-testid="testimonial-upload-button" variant="outline" disabled={uploading} onClick={() => document.getElementById("t-image-file").click()} className="border-[#A67C1E]/50 text-[#A67C1E] bg-transparent hover:bg-[#A67C1E]/10 shrink-0">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Unggah
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="t-active" data-testid="testimonial-active-switch" checked={form.active} onCheckedChange={(v) => set("active")(v)} />
              <Label htmlFor="t-active" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Tampilkan di beranda</Label>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="testimonial-cancel-button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5">
              Batal
            </Button>
            <Button data-testid="testimonial-save-button" onClick={handleSave} disabled={saving} className="bg-[#A67C1E] text-[#FBF7EE] hover:bg-[#211B10] font-medium">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Simpan Perubahan" : "Tambah Testimoni"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
